import { Logo } from '@/components/Logo';
import Footer from '@/renderer/Footer';

/**
 * Shared chrome for the standalone auth screens (sign in, reset password): a
 * centered branded card on a soft forest-green backdrop, with the app footer
 * below. Keeps the auth pages visually in lockstep so they can't drift apart.
 */
export function AuthShell({
  children,
  hideLogo = false,
}: {
  children: React.ReactNode;
  hideLogo?: boolean;
}) {
  return (
    <div className="relative flex min-h-full flex-col bg-[Canvas]">
      {/* Branded backdrop: soft forest-green glow + very faint grid, masked to fade out */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(127,127,127,0.09) 1px, transparent 1px), linear-gradient(to bottom, rgba(127,127,127,0.09) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            WebkitMaskImage:
              'radial-gradient(120% 90% at 50% 0%, #000 0%, rgba(0,0,0,0.4) 45%, transparent 78%)',
            maskImage:
              'radial-gradient(120% 90% at 50% 0%, #000 0%, rgba(0,0,0,0.4) 45%, transparent 78%)',
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-[420px]"
          style={{
            background:
              'radial-gradient(60% 70% at 50% -8%, rgba(21,115,74,0.18) 0%, rgba(21,115,74,0.08) 38%, transparent 72%)',
          }}
        />
      </div>

      <div className="relative flex flex-1 items-center justify-center px-6 py-12 sm:px-6">
        {/* On narrow/native screens the form goes edge-to-edge with no card
            framing — a bordered card floating inside an already-full-screen
            mobile viewport reads as redundant. From sm up it becomes the
            centered branded card (border + shadow + max-width). */}
        <div className="w-full max-w-[390px] rounded-none border-0 bg-transparent p-0 shadow-none sm:rounded-2xl sm:border sm:border-zinc-950/10 sm:bg-white sm:p-8 sm:shadow-lg dark:bg-transparent dark:shadow-none sm:dark:border-white/10 sm:dark:bg-zinc-900/70 sm:dark:shadow-black/40 sm:dark:backdrop-blur-sm">
          {!hideLogo && (
            <div className="mb-5 flex justify-center">
              <a
                href="/"
                aria-label="RAS home"
                className="block size-[58px] select-none transition hover:opacity-90"
              >
                <Logo className="size-full" width={58} />
              </a>
            </div>
          )}
          {children}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/** Full-width forest-green primary button styling shared across auth forms. */
export const authPrimaryButtonClass =
  'w-full bg-[#15734A] font-bold hover:bg-[#125f3d] focus-visible:outline-[#15734A] dark:bg-[#34B473] dark:text-zinc-950 dark:hover:bg-[#43c983]';

/** Forest-green inline link styling shared across auth forms. */
export const authLinkClass = 'font-semibold text-[#15734A] dark:text-[#34B473]';
